import {User} from "./user";
import {UserSubscription, UserSubscriptionType} from "./user-subscription";
import {UserState} from "./user-state";
import {BackupPermissions, ImageOptimizerPermissions, Permissions} from "./permissions";
import {Utils} from "../shared/utils/utils";
import {TypeUtils} from "../shared/utils/type-utils";
import {Workspace} from "../user-managment/workspace";
import {SignUpFor, TypeFormAnswers} from "../add-website/typeFormAnswers";

export class UserMapper {
  public static typeFormAnswers = TypeFormAnswers;

  public static mapUserData(data: any): User {
    const user: User = new User();
    user.id = data.id;
    user.firstName = data.first_name;
    user.lastName = data.last_name;
    user.email = data.email;
    let signupfor;
    if (data.sign_up_for) {
      try {
        signupfor = JSON.parse(data.sign_up_for);
      } catch (e) {
        signupfor = null;
      }
    }
    user.signUpFor = signupfor && UserMapper.signUpForMapper(signupfor) || null;
    user.agencyOrFreelancer = user.signUpFor && user.signUpFor.Role && user.signUpFor.Role.id && ([this.typeFormAnswers.AGENCY, this.typeFormAnswers.FREELANCE].includes(user.signUpFor.Role.id) || (![TypeFormAnswers.AGENCY, TypeFormAnswers.FREELANCE].includes(user.signUpFor.Role.id) && user.signUpFor.BuildingFor && user.signUpFor.BuildingFor.id && user.signUpFor.BuildingFor.id === this.typeFormAnswers.BUILD_MY_CLIENT));
    user.username = data.username;
    user.phoneNumber = data.phone_number;
    user.timeZone = data.timezone;
    user.timeZoneOffset = data.timezone_offset || 0;
    user.companyName = data.company_name;
    user.companyVat = data.company_vat;
    user.companyAddressLine1 = data.address_line_1;
    user.companyAddressLine2 = data.address_line_2;
    user.companyCity = data.city;
    user.companyState = "";
    user.companyCountry = data.country;
    user.companyZipCode = data.post_code;
    user.companyWebsite = data.company_website;
    user.managerActivated = Utils.isTruthy(data.manager_activated);
    user.lastLogin = data.last_login ? data.last_login : "";

    return user;
  }

  public static map(data: any, subscription: UserSubscription): User {
    const user: User = new User();
    user.id = data.id;
    user.firstName = data.first_name;
    user.lastName = data.last_name;
    user.email = data.email;
    let signupfor;
    if (data.sign_up_for) {
      try {
        signupfor = JSON.parse(data.sign_up_for);
      } catch (e) {
        signupfor = null;
      }
    }
    user.createdAt = data.created_at;
    user.signUpFor = signupfor && UserMapper.signUpForMapper(signupfor) || null;
    user.agencyOrFreelancer = user.signUpFor && user.signUpFor.Role && user.signUpFor.Role.id && ([this.typeFormAnswers.AGENCY, this.typeFormAnswers.FREELANCE].includes(user.signUpFor.Role.id) || (user.signUpFor.BuildingFor && user.signUpFor.BuildingFor.id === this.typeFormAnswers.BUILD_MY_CLIENT));
    user.username = data.username;
    user.phoneNumber = data.phone_number;
    user.timeZone = data.timezone;
    user.timeZoneOffset = data.timezone_offset || 0;
    user.companyName = data.company_name;
    user.companyVat = data.company_vat;
    user.companyAddressLine1 = data.address_line_1;
    user.companyAddressLine2 = data.address_line_2;
    user.companyCity = data.city;
    user.companyState = "";
    user.companyCountry = data.country;
    user.companyZipCode = data.post_code;
    user.companyWebsite = data.company_website;
    user.hasHostingTrial = subscription ? Utils.isTruthy(subscription.hasHostingTrial) : false;
    user.hostingTrialExpired = subscription && !!subscription.hostingTrialExpired;
    user.hostingTrialExpirationDate = subscription && subscription.hostingTrialExpireDate ? Utils.getDate(subscription.hostingTrialExpireDate) : null;
    user.managerActivated = Utils.isTruthy(data.manager_activated);
    user.state = UserMapper.parseUserState(user, subscription);
    user.permissions = UserMapper.parseUserPermissions(user, subscription, subscription);
    user.planTitle = subscription && subscription.plan ? subscription.plan.title : "";
    user.planProductSlug = subscription && subscription.plan && subscription.plan.description && subscription.plan.description.description && subscription.plan.description.description.premiumPluginKey ? subscription.plan.description.description.premiumPluginKey : "";
    user.planType = subscription && subscription.type ? subscription.type : null;
    user.apiResellerFree = subscription && subscription.apiResellerFree ? subscription.apiResellerFree : null;
    user.starterClient = user.planType === UserSubscriptionType.FREE;
    user.platformClient = user.planType === UserSubscriptionType.PLATFORM || user.planType === UserSubscriptionType.DEDICATEDVM;
    user.oneProductOrBundleClient = [UserSubscriptionType.PREMIUM, UserSubscriptionType.ONE_PRODUCT].includes(<any>user.planType);
    user.category = subscription && subscription.category ? subscription.category : null;
    user.lastLogin = data.last_login ? data.last_login : "";
    user.hosting_services = subscription && subscription.plan && subscription.plan.services.hosting ? subscription.plan.services.hosting : null;
    user.backup_services = subscription && subscription.plan && subscription.plan.services.Backup ? subscription.plan.services.Backup : null;
    user.connectedFromProductId = subscription && subscription.product_id ? parseInt(subscription.product_id, 10) : 0;

    user.agreementDate = subscription ? new Date(subscription.agreementDate).getTime() : "";
    user.subscriptionId = subscription ? subscription.subscriptionId : null;
    user.additionalWebsiteCount = subscription && subscription.plan && subscription.plan.additionalDomainCount ? subscription.plan.additionalDomainCount : 0;

    user.agreementId = subscription && subscription.id ? subscription.id : 0;
    user.twoFaStatus = data['google_2fa_status'] ? data['google_2fa_status'] : false;
    user.emailVerified = Utils.isTruthy(data.verified);
    user.allowGrantAccess = Utils.isTruthy(data.allow_grant_access);
    user.nps = Utils.isTruthy(data.nps);
    user.websiteConnectedSpeed = data.speed_website_connected > 0 || false;
    user.websiteConnected = data.website_connected > 0 || false; // Utils.isTruthy(data.website_connected, true);
    user.websiteCreated = data.website_created || 0; // Utils.isTruthy(data.website_created);
    user.magicLinkSent = data.magic_link_sent_at;
    user.planPrice = subscription && subscription.plan && subscription.plan.price;
    user.set_source = data.set_source;
    user.labels = data.labels || [];
    user.hasExpertsProfile = data.labels.includes('has-expert-profile') || false;
    user.workspaces = UserMapper.workspaceMap(data.workspaces);
    user.aiWebsiteConnected = data.ai_website_connected;
    user.referralHash = data.referral_hash;
    user.isSuspected = data.is_blocked === 2;

    return user;
  }

  public static workspaceMap(workspaces) {
    const workspacesState: Workspace[] =  workspaces.map(workspace => {
      const workspaceState = new Workspace();
      workspaceState.workspaceId = workspace.workspace_id;
      workspaceState.workspaceName = workspace.workspace_name;
      workspaceState.workspaceLogo = workspace.workspace_logo;
      workspaceState.workspaceColor = workspace.workspace_color || "#59C66A";
      workspaceState.status = workspace.workspace_status;
      workspaceState.roleId = workspace.role_id;
      workspaceState.roleSlug = workspace.role_slug;
      workspaceState.roleName = workspace.role_name;
      return workspaceState;
    });

    return workspacesState;
  }

  public static parseUserState(user: User, subscription: UserSubscription): UserState {
    const userState: UserState = new UserState();
    userState.noActiveSubscription = !subscription;
    userState.noActiveSubscription = !subscription;
    userState.subscriptionExpired = subscription && subscription.isExpired;
    userState.stripeSbcrstatus = subscription && subscription.stripeSbcrstatus;
    userState.subscriptionBillingType = subscription && subscription.is_non_billable ? 'not-paid' : 'paid';
    userState.hasActiveDomainAgreement = subscription && subscription.hasActiveDomainAgreement || false;
    userState.paymentFailed = subscription && subscription.paymentFailed;

    return userState;
  }

  private static parseUserPermissions(user: User, subscription: UserSubscription, agreementData: any): Permissions {
    const permissions: Permissions = new Permissions();

    permissions.canViewContent = true;
    permissions.canManageContent = true;
    if (!subscription || (subscription && (((subscription.subscriptionId === 211 || subscription.subscriptionId === 257) && user.hostingTrialExpired)))) {
      permissions.canManageContent = false;
      permissions.canViewContent = false;
    }

    const backupServiceData: any = agreementData && agreementData.plan ? agreementData.plan.services.Backup : null;
    const backupPermissions: BackupPermissions = new BackupPermissions();
    backupPermissions.canDownloadBackup = backupServiceData ? this.isFeatureAvailable(backupServiceData, 'backup_download') : false;
    backupPermissions.realTimeSchedulingAvailable = backupServiceData ?  this.isFeatureAvailable(backupServiceData, 'real_time_scheduling') : false;
    backupPermissions.canManageBackupSettings = backupServiceData ? this.isFeatureAvailable(backupServiceData, 'backup_settings') : false;
    backupPermissions.canManageRestoreSettings = backupServiceData ? this.isFeatureAvailable(backupServiceData, 'restore_settings') : false;
    backupPermissions.storage = backupServiceData ? parseInt(backupServiceData.storage.parameter_value, 10) : 0;
    backupPermissions.storageUnit = backupServiceData ? backupServiceData.storage.unit : "B";
    permissions.backup = backupPermissions;

    const imageOptimizerServiceData: any = agreementData && agreementData.plan ? agreementData.plan.services.optimizer : null;
    const imageOptimizerPermissions: ImageOptimizerPermissions = new ImageOptimizerPermissions();
    imageOptimizerPermissions.extremeModeAvailable = imageOptimizerServiceData ? this.isFeatureAvailable(imageOptimizerServiceData, 'extreme_mode') : false;
    imageOptimizerPermissions.autoOptimizationAvailable = imageOptimizerServiceData ? this.isFeatureAvailable(imageOptimizerServiceData, 'auto_optimization') : false;
    imageOptimizerPermissions.limit = imageOptimizerServiceData ? parseInt(imageOptimizerServiceData.limit.parameter_value, 10) : 0;
    permissions.imageOptimizer = imageOptimizerPermissions;

    return permissions;
  }

  private static isFeatureAvailable(serviceData: any, feature: string): boolean {
    if (TypeUtils.isUndefined(serviceData)) {
      return true;
    }

    if (TypeUtils.isUndefined(serviceData[feature])) {
      return true;
    }

    return Utils.isTruthy(serviceData[feature].parameter_value);
  }

  public static convertToApiFormat(user: any, productId): any {
    const data = {
      address_line_1: user.companyAddressLine1 ? user.companyAddressLine1 : '',
      address_line_2: user.companyAddressLine2 ? user.companyAddressLine2 : '',
      city: user.companyCity ? user.companyCity : '',
      company_name: user.companyName ? user.companyName : '',
      company_vat: user.companyVat ? user.companyVat : '',
      company_website: user.companyWebsite ? user.companyWebsite : '',
      country: user.companyCountry ? user.companyCountry : '',
      email: user.email ? user.email : '',
      id: user.id ? user.id : '',
      manager_activated: user.managerActivated ? user.managerActivated : '',
      first_name: user.firstName ? user.firstName : '',
      last_name: user.lastName ? user.lastName : '',
      phone_number: user.phoneNumber ? user.phoneNumber : '',
      post_code: user.companyZipCode ? user.companyZipCode : '',
      timezone: user.timeZone ? user.timeZone : '',
      password: user.password ? user.password : '',
      username: user.username ? user.username : user.email,
      ga_client_cookie: user.ga_client_cookie ? user.ga_client_cookie : null,
      product_id: productId,
      affiliate_id: user.affiliate_id
    };

    if (user.labels) {
      data['labels'] = JSON.stringify(user.labels);
    }

    return data;
  }

  public static contactUsConvertToApiFormat(formData: any): any {
    return {
      'email': formData.email,
      'subject': formData.subject,
      'full_name': formData.fullName,
      'inquiry_type': formData.inquiryType,
      'product': formData.customerServiceSupport,
      'message': formData.message
    };
  }

  public static signUpForMapper(data): SignUpFor {
    const signUpFor: SignUpFor = new SignUpFor();

    signUpFor.Role = data['question-role'];
    signUpFor.Industry = data['question-industry'];
    signUpFor.Reason = data['question-reason'];
    signUpFor.WebsiteNumber = data['question-website-number'];
    signUpFor.CreationOption = data['question-creation-option'];
    signUpFor.WhoBuilds = data['question-who-builds'] || null;
    signUpFor.BuildingFor = data['question-building-for'] || null;
    signUpFor.FirstChosenOption = data['question-first-chosen-option'] || null;
    signUpFor.LastChosenOption = data['question-last-chosen-option'] || null;
    signUpFor.Action = data['question-action'] || null;
    signUpFor.Source = data['question-source'] || null;

    return signUpFor;
  }

}
